import React, { useState, useMemo } from 'react';
import ReCAPTCHA from "react-google-recaptcha";
import BinaryQuestion from '../components/BinaryQuestion';
import DynamicRadioInput from '../components/DynamicRadioInput';
import { useLanguage } from "../context/LanguageContext";

interface FormData {
    direitoArrependimento: string;
    descumprimentoOferta: string;
    extravioDefinitivo: string;
    faixaIntervaloExtravioTemporario: string;
    violacaoFurtoAvaria: string;
    intervaloAtraso: string;
    noshow: string;
    overbooking: string;
    desamparo: string;
    hipervulneravel: string;
}

export default function SecondForm() {
    const [captcha, setCaptcha] = useState<String | null>(null);
    const [formData, setFormData] = useState<FormData>({
        direitoArrependimento: '',
        descumprimentoOferta: '',
        extravioDefinitivo: '',
        faixaIntervaloExtravioTemporario: '',
        violacaoFurtoAvaria: '',
        intervaloAtraso: '',
        noshow: '',
        overbooking: '',
        desamparo: '',
        hipervulneravel: '',
    });
    const [response, setResponse] = useState('')
    const [responseSuccess, setResponseSuccess] = useState(false)
    const [shapImage, setShapImage] = useState('');
    const [checkboxChecked, setCheckboxChecked] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const { lang, t } = useLanguage();

    const faixaIntervaloExtravioTemporario = useMemo(() => [
        { value: '1', displayedName: "1h - 24h"},
        { value: '2', displayedName: "25h - 72h"},
        { value: '3', displayedName: "73h - 168h"},
        { value: '4', displayedName: t.secondForm.acima169h},
        { value: '0', displayedName: t.secondForm.naoHouveExtravio},
    ], [t]); // Depende apenas de 't'. Se o idioma mudar, o useMemo é reexecutado.

    const intervaloAtraso = useMemo(() => [
        { value: '0', displayedName: t.secondForm.naoHouveProblemas},
        { value: '1', displayedName: "1h - 3h59m"},
        { value: '2', displayedName: "4h - 7h59m"},
        { value: '3', displayedName: "8h - 11h59m"},
        { value: '4', displayedName: "12h - 15h59m"},
        { value: '5', displayedName: "16h - 23h59m"},
        { value: '6', displayedName: "24h - 27h59m"},
        { value: '7', displayedName: t.secondForm.acima28h},
        { value: '-1', displayedName: t.secondForm.naoChegueiDestino}
    ], [t]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();

      const payload = {
          ...formData,
          linguagem: lang
      };

      try {
          // Fetch for prod:
          const response = await fetch('https://api.concilia.ufsc.br/predict', {
          // Fetch for dev:
          //const response = await fetch('http://127.0.0.1:5000/predict', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          });

        const result = await response.json();
        if (!response.ok) {
          const errorMsg = result['error']
          throw new Error(errorMsg);
        }

        setResponseSuccess(true)
        setResponse(String(result['prediction']));
        setShapImage(result['Explanation']);

    
        } catch (error) {
          console.error('There was a problem with the fetch operation:', error);
          setResponseSuccess(false)
          setResponse(t.secondForm.requestError);
        }
    };

    return (
    <div>
      <form onSubmit={handleSubmit} className="my-4">
        {/* Radio buttons */}
        <div className="px-10 space-y-9">
          <BinaryQuestion
            question={
              <>
                <strong>{t.secondForm.direitoArrependimentoQuestion}</strong> {`- ${t.secondForm.direitoArrependimentoText}`}
              </>
            }
            name="direitoArrependimento"
            value={formData.direitoArrependimento}
            onChange={handleChange}
          />
          <BinaryQuestion
            question={
              <>
                <strong>{t.secondForm.descumprimentoOfertaQuestion}</strong> {`- ${t.secondForm.descumprimentoOfertaText}`}
              </>
            }
            name="descumprimentoOferta"
            value={formData.descumprimentoOferta}
            onChange={handleChange}
          />
          <BinaryQuestion
            question={
              <>
                <strong>{t.secondForm.extravioDefinitivoQuestion}</strong> {`- ${t.secondForm.extravioDefinitivoText}`}
              </>
            }
            name="extravioDefinitivo"
            value={formData.extravioDefinitivo}
            onChange={handleChange}
          />
          <DynamicRadioInput
            question={
              <>
                <strong>{t.secondForm.extravioTemporarioQuestion}</strong> {`- ${t.secondForm.extravioTemporarioText}`}
              </>
            }
            name="faixaIntervaloExtravioTemporario"
            value={formData.faixaIntervaloExtravioTemporario}
            onChange={handleChange}
            options={faixaIntervaloExtravioTemporario}
          />
          <BinaryQuestion
            question={
              <>
                <strong>{t.secondForm.violacaoBagagemQuestion}</strong> {`- ${t.secondForm.violacaoBagagemText}`}
              </>
            }
            name="violacaoFurtoAvaria"
            value={formData.violacaoFurtoAvaria}
            onChange={handleChange}
          />
          <BinaryQuestion
            question={
              <>
                <strong>{t.secondForm.hipervulnerabilidadeQuestion}</strong> {`- ${t.secondForm.hipervulnerabilidadeText}`}
              </>
            }
            name="hipervulneravel"
            value={formData.hipervulneravel}
            onChange={handleChange}
          />
          <DynamicRadioInput
            question={
              <>
                <strong>{t.secondForm.atrasoCancelamentoQuestion}</strong>
                <ul>
                  <li>{`- ${t.secondForm.atrasoChegadaText}`}</li>
                  <li>{`- ${t.secondForm.atrasoDestinoAlteradoText}`}</li>
                  <li>{`- ${t.secondForm.atrasoSemProblemasText}`}</li>
                </ul>
              </>
            }
            value={formData.intervaloAtraso}
            onChange={handleChange}
            options={intervaloAtraso}
            name="intervaloAtraso"
          />
          <BinaryQuestion
            question={
              <>
                <strong>{t.secondForm.noshowQuestion}</strong> {`- ${t.secondForm.noshowText}`}
              </>
            }
            name="noshow"
            value={formData.noshow}
            onChange={handleChange}
          />
          <BinaryQuestion
            question={
              <>
                <strong>{t.secondForm.overbookingQuestion}</strong> {`- ${t.secondForm.overbookingText}`}
              </>
            }
            name="overbooking"
            value={formData.overbooking}
            onChange={handleChange}
          />
          <BinaryQuestion
            question={
              <>
                <strong>{t.secondForm.falhaAssistenciaQuestion}</strong>
                <p>{t.secondForm.falhaAssistenciaTitle}</p>
                <ul>
                  <li>{`- ${t.secondForm.falhaAssistenciaNaoPrestou}`}</li>
                  <li>{`- ${t.secondForm.falhaAssistenciaTardiamente}`}</li>
                  <li>{`- ${t.secondForm.falhaAssistenciaInsuficiente}`}</li>
                </ul>
                <p>{t.secondForm.falhaAssistenciaNao}</p>
              </>
            }
            name="desamparo"
            value={formData.desamparo}
            onChange={handleChange}
          />
          <div className="w-fit mx-auto">
            <ReCAPTCHA
              sitekey={'6Lcg6VAsAAAAAFseXw3BRnbYh4MlSzvWJKZUV8EE'}
              onChange={(val) => setCaptcha(val)}
            />
          </div>

          <div className="flex flex-col items-center mt-4 space-y-3">
            {/* Checkbox com Modal */}
            <div className="text-sm text-gray-700">
              <label className="flex items-start space-x-2">
                <input
                  type="checkbox"
                  checked={checkboxChecked}
                  onChange={(e) => setCheckboxChecked(e.target.checked)}
                  className="accent-blue-600 mt-1"
                  required
                />
                <span>
                  {t.secondForm.declaracaoCiente}{' '}
                  <button
                    type="button"
                    onClick={() => setShowModal(true)}
                    className="text-blue-600 underline hover:text-blue-800"
                  >
                    {t.secondForm.verDetalhes}
                  </button>
                </span>
              </label>
            </div>

            {/* Modal */}
            {showModal && (
              <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-lg shadow-lg max-w-lg w-full relative text-sm space-y-3">
                  <h2 className="text-lg font-semibold">{t.secondForm.termosTitle}</h2>
                  <h2 className="text-base font-semibold">{t.secondForm.modeloCienteTitle}</h2>
                  <ul className="list-disc ml-5 space-y-2 text-gray-700">
                    <li>{t.secondForm.termoInformativo}</li>
                    <li>{t.secondForm.termoInesperado}</li>
                    <li>{t.secondForm.termoTreinamento}</li>
                  </ul>
                  <div className="flex justify-end">
                    <button
                      onClick={() => setShowModal(false)}
                      className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      {t.secondForm.fechar}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <button
              disabled={!captcha || !checkboxChecked}
              type="submit"
              className={`px-6 py-2 rounded font-semibold transition-colors ${
                captcha && checkboxChecked
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-600 text-white opacity-60 cursor-not-allowed'
              }`}
            >
              {t.secondForm.consultar}
            </button>
          </div>
        </div>
      </form>

      {response && (
        <div
          className={`flex justify-center text-base font-medium my-8 p-4 border-t-4 rounded ${
            responseSuccess ? 'bg-green-100 border-green-500 text-green-700' : 'bg-red-100 border-red-500 text-red-700'
          }`}
        >
          <p>
            {responseSuccess && `${t.secondForm.predictionTitle} `}
            {response}
          </p>
        </div>
      )}

      {/* Display the SHAP image after the prediction */}
      {shapImage && (
        <div className="flex flex-col items-center px-8">
          <img src={`data:image/png;base64,${shapImage}`} alt="SHAP Explanation" className="mb-6" />
          <div className="max-w-3xl text-gray-700 text-justify text-base space-y-3 mb-8">
            <p className="font-semibold text-center">{t.secondForm.fatoresInfluenciaTitle}</p>
            <p>{t.secondForm.fatoresInfluenciaText1}</p>
            <p>
              <span className="text-blue-600 font-semibold">{t.secondForm.fatoresInfluenciaText2Blue[0]}</span>{t.secondForm.fatoresInfluenciaText2Blue[1]}
              <br />
              <span className="text-red-600 font-semibold">{t.secondForm.fatoresInfluenciaText2Red[0]}</span>{t.secondForm.fatoresInfluenciaText2Red[1]}
            </p>
            <p>{t.secondForm.fatoresInfluenciaText3}</p>
            <p>{t.secondForm.fatoresInfluenciaText4[0]}<strong>{t.secondForm.fatoresInfluenciaText4[1]}</strong>{t.secondForm.fatoresInfluenciaText4[2]}<em>{t.secondForm.fatoresInfluenciaText4[3]}</em>{t.secondForm.fatoresInfluenciaText4[4]}</p>
            <p>
              {t.secondForm.fatoresInfluenciaText5}
              <a href="https://shap.readthedocs.io/en/latest/" className="text-blue-600 underline">
                https://shap.readthedocs.io/en/latest/
              </a>
              .
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

///// Fim do código.